import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  serverTimestamp,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseDb, getFirebaseFunctions } from './firebase';
import { handleAuthError } from './authRecoveryService';
import type {
  Invitation,
  InvitationStatus,
  UserRole,
} from '../types/settings';

// ============================================================================
// TOKEN GENERATION
// ============================================================================

/**
 * Generate a secure random token for invitations
 */
function generateInvitationToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Hash a token using SHA-256 (for secure storage)
 * Tokens are hashed before storage to prevent exposure if database is compromised
 */
async function hashToken(token: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(token);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

// ============================================================================
// INVITATION CRUD
// ============================================================================

/**
 * Create a new invitation
 */
export async function createInvitation(
  orgId: string,
  email: string,
  role: UserRole,
  invitedBy: string,
  inviterName?: string
): Promise<string> {
  const db = getFirebaseDb();

  // Check if there's already a pending invitation for this email
  const existingInvite = await getInvitationByEmail(orgId, email);
  if (existingInvite && existingInvite.status === 'pending') {
    throw new Error('An invitation has already been sent to this email');
  }

  const inviteRef = doc(collection(db, 'organizations', orgId, 'invitations'));
  const inviteId = inviteRef.id;
  const token = generateInvitationToken();
  const tokenHash = await hashToken(token);

  // Invitation expires in 7 days
  const expiresAt = Timestamp.fromDate(
    new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  );

  await setDoc(inviteRef, {
    id: inviteId,
    email: email.toLowerCase(),
    role,
    invitedBy,
    inviterName,
    tokenHash, // Store hash, not plaintext
    status: 'pending' as InvitationStatus,
    createdAt: serverTimestamp(),
    expiresAt,
  });

  return token; // Return plaintext for the invite link
}

/**
 * Get all invitations for an organization
 */
export async function getInvitations(orgId: string): Promise<Invitation[]> {
  const db = getFirebaseDb();
  const invitationsRef = collection(db, 'organizations', orgId, 'invitations');
  const snapshot = await getDocs(invitationsRef);

  return snapshot.docs.map((doc) => doc.data() as Invitation);
}

/**
 * Get pending invitations only
 */
export async function getPendingInvitations(orgId: string): Promise<Invitation[]> {
  const db = getFirebaseDb();
  const invitationsRef = collection(db, 'organizations', orgId, 'invitations');
  const q = query(invitationsRef, where('status', '==', 'pending'));
  const snapshot = await getDocs(q);

  // Include expired invitations so they can be renewed
  return snapshot.docs.map((doc) => doc.data() as Invitation);
}

/**
 * Subscribe to pending invitations (real-time updates)
 */
export function subscribePendingInvitations(
  orgId: string,
  callback: (invitations: Invitation[]) => void
): () => void {
  const db = getFirebaseDb();
  const invitationsRef = collection(db, 'organizations', orgId, 'invitations');
  const q = query(invitationsRef, where('status', '==', 'pending'));

  // Include expired invitations so they can be renewed
  return onSnapshot(
    q,
    (snapshot) => {
      const invitations = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Invitation[];
      callback(invitations);
    },
    async (error) => {
      console.error('[InvitationService] Subscription error:', error);
      await handleAuthError(error);
    }
  );
}

/**
 * Get invitation by email
 */
async function getInvitationByEmail(
  orgId: string,
  email: string
): Promise<Invitation | null> {
  const db = getFirebaseDb();
  const invitationsRef = collection(db, 'organizations', orgId, 'invitations');
  const q = query(
    invitationsRef,
    where('email', '==', email.toLowerCase()),
    where('status', '==', 'pending')
  );
  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  return snapshot.docs[0].data() as Invitation;
}

// ============================================================================
// INVITATION ACCEPTANCE
// ============================================================================

/**
 * Get invitation preview details (email, org name) from a token
 * Does NOT require authentication - allows users to see invitation details before signing up
 */
export async function getInvitationPreview(
  token: string
): Promise<{ email: string; orgName: string; role: string; inviterName: string | null }> {
  const functions = getFirebaseFunctions();
  const getPreviewFn = httpsCallable<
    { token: string },
    { email: string; orgName: string; role: string; inviterName: string | null }
  >(functions, 'getInvitationPreview');

  const result = await getPreviewFn({ token });
  return result.data;
}

/**
 * Accept an invitation and join the organization via Cloud Function
 * Server-side acceptance:
 * 1. Uses collectionGroup query to find token (efficient - no org scanning)
 * 2. Validates invitation (pending, not expired)
 * 3. Creates member doc (Admin SDK - bypasses rules)
 * 4. Updates user organizationId/role (Admin SDK - bypasses rules)
 * 5. Marks invitation as accepted
 *
 * This fixes the getAfter() vulnerability and the inefficient token search.
 */
export async function acceptInvitation(
  token: string,
  _userId: string
): Promise<{ orgId: string; orgName: string }> {
  const functions = getFirebaseFunctions();
  const acceptInviteFn = httpsCallable<
    { token: string },
    { success: boolean; orgId: string; orgName: string }
  >(functions, 'acceptInvitationFunction');

  const result = await acceptInviteFn({ token });
  return {
    orgId: result.data.orgId,
    orgName: result.data.orgName,
  };
}

/**
 * Revoke (delete) a pending invitation
 */
export async function revokeInvitation(
  orgId: string,
  inviteId: string
): Promise<void> {
  const db = getFirebaseDb();
  await deleteDoc(doc(db, 'organizations', orgId, 'invitations', inviteId));
}

/**
 * Resend an invitation (generates new token and expiry)
 */
export async function resendInvitation(
  orgId: string,
  inviteId: string
): Promise<string> {
  const db = getFirebaseDb();
  const newToken = generateInvitationToken();
  const tokenHash = await hashToken(newToken);
  const expiresAt = Timestamp.fromDate(
    new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  );

  await updateDoc(doc(db, 'organizations', orgId, 'invitations', inviteId), {
    tokenHash, // Store hash, not plaintext
    expiresAt,
    status: 'pending' as InvitationStatus,
  });

  return newToken; // Return plaintext for the invite link
}

// ============================================================================
// INVITATION HELPERS
// ============================================================================

/**
 * Get invitation details by ID
 */
export async function getInvitation(
  orgId: string,
  inviteId: string
): Promise<Invitation | null> {
  const db = getFirebaseDb();
  const inviteDoc = await getDoc(
    doc(db, 'organizations', orgId, 'invitations', inviteId)
  );

  if (!inviteDoc.exists()) {
    return null;
  }

  return inviteDoc.data() as Invitation;
}

/**
 * Check if user was invited to any organization (by email)
 */
export async function checkPendingInvitationsForUser(
  email: string
): Promise<{ invitation: Invitation; orgId: string; orgName: string }[]> {
  const db = getFirebaseDb();
  const results: { invitation: Invitation; orgId: string; orgName: string }[] = [];

  // Search all organizations for invitations to this email
  const orgsSnapshot = await getDocs(collection(db, 'organizations'));

  for (const orgDoc of orgsSnapshot.docs) {
    const invitation = await getInvitationByEmail(orgDoc.id, email);
    if (invitation && invitation.expiresAt.toDate() > new Date()) {
      results.push({
        invitation,
        orgId: orgDoc.id,
        orgName: orgDoc.data().name,
      });
    }
  }

  return results;
}
